import React from 'react';
import { motion } from 'framer-motion';
import { FiFolderPlus, FiGitBranch, FiTag, FiCheckCircle, FiClock, FiUsers, FiSettings, FiEye, FiBarChart, FiTrendingUp, FiShield } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import MobileFeatureCarousel from '../components/MobileFeatureCarousel';
import ImageModal from '../components/ImageModal';

const Projects = () => {
  const [imageModal, setImageModal] = React.useState<{
    isOpen: boolean;
    src: string;
    alt: string;
    title?: string;
  }>({
    isOpen: false,
    src: '',
    alt: '',
    title: ''
  });

  const openImageModal = (src: string, alt: string, title?: string) => {
    setImageModal({ isOpen: true, src, alt, title });
  };

  const closeImageModal = () => {
    setImageModal({ isOpen: false, src: '', alt: '', title: '' });
  };

  const projectStats = [
    { label: "Total Projects", value: "33", description: "projects under security management", icon: <FiFolderPlus className="w-8 h-8" /> },
    { label: "Active Repositories", value: "28", description: "repositories with recent activity", icon: <FiGitBranch className="w-8 h-8" /> },
    { label: "Security Scans", value: "120", description: "total scans across all projects", icon: <FiShield className="w-8 h-8" /> },
    { label: "Last Updated", value: "2", description: "hours ago", icon: <FiClock className="w-8 h-8" /> }
  ];

  const projectFeatures = [
    {
      title: "Project Card Layout",
      description: "Organized view of all your security projects with visual cards, status indicators, and quick access to project details.",
      icon: <FiFolderPlus className="w-8 h-8" />,
      details: [
        "Visual project cards with descriptions and metadata",
        "Project status indicators and health scores",
        "Quick access to project details and settings",
        "Filtering and sorting capabilities"
      ]
    },
    {
      title: "Repository Integration",
      description: "Seamless connection to your code repositories with direct links and automatic project discovery capabilities.",
      icon: <FiGitBranch className="w-8 h-8" />,
      details: [
        "Direct links to GitHub, Bitbucket, and GitLab repositories",
        "Integration status monitoring and health checks",
        "Automatic project discovery from connected repositories",
        "Branch and commit tracking for security scans"
      ]
    },
    {
      title: "Scan History & Analytics",
      description: "Comprehensive tracking of security scanning activities with trend analysis and progress monitoring.",
      icon: <FiBarChart className="w-8 h-8" />,
      details: [
        "Complete scan history with timestamps and results",
        "Security trend analysis and progress tracking",
        "Vulnerability discovery and resolution metrics",
        "Compliance status and reporting capabilities"
      ]
    }
  ];

  const projectTypes = [
    {
      type: "Web Applications",
      description: "Frontend and backend web application projects",
      scanTypes: ["SAST", "DAST", "SCA", "Secrets Detection"],
      examples: ["React applications", "Node.js APIs", "Django backends", "Spring Boot services"],
      icon: "🌐"
    },
    {
      type: "Mobile Applications",
      description: "iOS and Android mobile application projects",
      scanTypes: ["SAST", "SCA", "API Security"],
      examples: ["React Native apps", "Flutter applications", "Native iOS/Android", "Hybrid apps"],
      icon: "📱"
    },
    {
      type: "Cloud Infrastructure",
      description: "Cloud-based infrastructure and deployment projects",
      scanTypes: ["Infrastructure Scan", "SBOM", "Threat Modeling"],
      examples: ["AWS deployments", "Kubernetes clusters", "Docker containers", "Terraform configs"],
      icon: "☁️"
    },
    {
      type: "API Services",
      description: "RESTful APIs and microservices projects",
      scanTypes: ["API Security", "SAST", "DAST", "SCA"],
      examples: ["REST APIs", "GraphQL services", "Microservices", "Serverless functions"],
      icon: "🔌"
    }
  ];

  const projectWorkflow = [
    {
      step: 1,
      title: "Project Discovery",
      description: "Automatically discover projects from connected repositories with comprehensive metadata collection.",
      icon: <FiEye className="w-8 h-8" />,
      details: [
        "Connect your GitHub, Bitbucket, or GitLab account",
        "Ana Security automatically discovers your repositories",
        "Projects are created with basic metadata and settings",
        "Review and customize project configurations as needed"
      ]
    },
    {
      step: 2,
      title: "Initial Security Scan",
      description: "Run comprehensive security scans on new projects to establish security baseline.",
      icon: <FiShield className="w-8 h-8" />,
      details: [
        "Select appropriate scan types based on project technology",
        "Configure scan settings and security policies",
        "Execute initial security assessment",
        "Review findings and establish security baseline"
      ]
    },
    {
      step: 3,
      title: "Ongoing Monitoring",
      description: "Continuous security monitoring and improvement with automated scanning and reporting.",
      icon: <FiBarChart className="w-8 h-8" />,
      details: [
        "Schedule regular automated security scans",
        "Monitor for new vulnerabilities and security issues",
        "Track security improvements and remediation progress",
        "Generate regular security reports and compliance updates"
      ]
    },
    {
      step: 4,
      title: "Security Optimization",
      description: "Optimize security posture based on findings, trends, and industry best practices.",
      icon: <FiTrendingUp className="w-8 h-8" />,
      details: [
        "Analyze security trends and patterns across projects",
        "Implement security improvements and best practices",
        "Update security policies and scan configurations",
        "Share security knowledge and lessons learned"
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8 px-4 sm:px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="Projects Management" />
        
        {/* Hero Section */}
        <div className="text-center mb-12">
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
            Organize and manage your security projects effectively. The Projects module provides a centralized view of all your applications under security management, with comprehensive tracking, analytics, and integration capabilities.
          </p>
          
          <div className="relative overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-900 mb-8">
            <img 
              src="/screenshots/projects/03_projects-overview.png" 
              alt="Projects Overview"
              className="w-full cursor-pointer hover:scale-105 transition-transform duration-300"
              onClick={() => openImageModal('/screenshots/projects/03_projects-overview.png', 'Projects Overview', 'Projects Management Dashboard')}
            />
          </div>
        </div>

        {/* Project Statistics - Responsive */}
        <div className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            Projects Dashboard - Current Statistics
          </h2>
          
          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {projectStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="text-center p-4 bg-gray-50 dark:bg-black rounded-lg"
              >
                <div className="text-primary-500 mb-3 flex justify-center">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {stat.label}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {stat.description}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden mb-8">
            <MobileFeatureCarousel 
              features={projectStats.map(stat => ({
                title: stat.label,
                description: stat.description,
                icon: stat.icon,
                details: [`Current Value: ${stat.value}`, stat.description]
              }))}
              autoPlay={true}
              autoPlayInterval={4000}
            />
          </div>

          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Projects Dashboard Features
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Card-Based Layout:</strong> Visual project cards with descriptions, tags, and status indicators</span>
              </div>
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Repository Links:</strong> Direct access to GitHub, Bitbucket, and GitLab repositories</span>
              </div>
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Scan History:</strong> Complete tracking of security scans and results</span>
              </div>
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Project Filtering:</strong> Search and filter projects by name, technology, or status</span>
              </div>
            </div>
          </div>
        </div>

        {/* Project Features - Responsive */}
        <div className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            Project Management Features
          </h2>
          
          {/* Desktop Layout */}
          <div className="hidden md:block space-y-6">
            {projectFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-primary-500">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.details?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700 dark:text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={projectFeatures}
              autoPlay={true}
              autoPlayInterval={6000}
            />
          </div>
        </div>

        {/* Project Types - Responsive Grid */}
        <div className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            Supported Project Types
          </h2>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-6">
            Ana Security supports various project types with tailored security scanning approaches:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {projectTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-4 bg-gray-50 dark:bg-black rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">{type.icon}</span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {type.type}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {type.description}
                </p>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Recommended Scans:</h4>
                  <div className="flex flex-wrap gap-2">
                    {type.scanTypes.map((scan, idx) => (
                      <span key={idx} className="px-2 py-1 bg-primary-500/10 text-primary-600 dark:text-primary-400 rounded text-xs">
                        {scan}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Examples:</h4>
                  <ul className="space-y-1">
                    {type.examples.map((example, idx) => (
                      <li key={idx} className="text-sm text-gray-600 dark:text-gray-400">
                        • {example}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Project Workflow - Responsive */}
        <div className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            Project Security Workflow
          </h2>
          
          {/* Desktop Layout */}
          <div className="hidden md:block space-y-8">
            {projectWorkflow.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-start gap-6 p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-primary-500 text-white rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                  {step.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {step.description}
                  </p>
                  <ul className="space-y-2">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={projectWorkflow.map(step => ({
                title: `Step ${step.step}: ${step.title}`,
                description: step.description,
                icon: step.icon,
                details: step.details
              }))}
              autoPlay={true}
              autoPlayInterval={8000}
            />
          </div>
        </div>

        {/* Call to Action - Responsive */}
        <div className="text-center p-8 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Ready to Organize Your Security Projects?
          </h2>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-8">
            Start managing your projects effectively with comprehensive security tracking and analytics.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <motion.a 
              href="/integrations" 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-3 p-4 bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500/30 transition-colors"
            >
              <FiGitBranch className="w-6 h-6 text-primary-500" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">Connect Repositories</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Link your GitHub, Bitbucket, or GitLab repositories</p>
              </div>
            </motion.a>
            
            <motion.a 
              href="/dashboard" 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-3 p-4 bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500/30 transition-colors"
            >
              <FiBarChart className="w-6 h-6 text-primary-500" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">View Dashboard</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Monitor project security metrics and trends</p>
              </div>
            </motion.a>
            
            <motion.a 
              href="/getting-started" 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-3 p-4 bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500/30 transition-colors"
            >
              <FiSettings className="w-6 h-6 text-primary-500" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">Get Started</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Learn how to set up your first security scan</p>
              </div>
            </motion.a>
          </div>
        </div>
      </motion.div>

      {/* Image Modal */}
      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={closeImageModal}
        src={imageModal.src}
        alt={imageModal.alt}
        title={imageModal.title}
      />
    </div>
  );
};

export default Projects;
